import { useEffect, useState, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { Search, Plus, RotateCcw } from 'lucide-react'
import { Badge } from '../../../../components/ui/badge'
import { Button } from '../../../../components/ui/button'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../../../components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../components/ui/dialog'
import { Input } from '../../../../components/ui/input'
import { Alert, AlertDescription } from '../../../../components/ui/alert'
import { TableLoadingState } from '../../../../components/ui/table-loading-state'
import { useAuth } from '../../../../contexts/AuthContext'
import { useWorkspace } from '../../../../contexts/WorkspaceContext'
import { ADMIN_PAGE_CONTENT_CLASSNAME } from '../layoutStyles'
import {
  fetchMembers,
  updateMember,
  toggleMemberStatus,
  createTeacher,
} from '../../../../api/backend'
import type { StudentItem, CreateMemberPayload } from '../../../../api/backend'

const PAGE_SIZE = 10

function genTempPassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  teacher: StudentItem
  onClose: () => void
  onSave: (updated: StudentItem) => void
}

function EditModal({ teacher, onClose, onSave }: EditModalProps) {
  const { getAccessToken } = useAuth()
  const [firstName, setFirstName] = useState(teacher.givenName ?? '')
  const [lastName, setLastName] = useState(teacher.surName ?? '')
  const [password, setPassword] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    const token = getAccessToken()
    if (!token) return
    setIsSaving(true)
    try {
      const payload: Parameters<typeof updateMember>[2] = {}
      payload.givenName = firstName.trim()
      payload.surName = lastName.trim()
      if (password.trim()) (payload as Record<string, unknown>).newPassword = password.trim()
      const updated = await updateMember(token, teacher.id, payload)
      onSave(updated)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save changes')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Edit Teacher</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                First Name
              </label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                Last Name
              </label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="grid gap-1.5">
            <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
              Username
            </label>
            <Input value={teacher.username ?? ''} disabled className="opacity-50 cursor-not-allowed" />
          </div>

          <div className="grid gap-1.5">
            <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
              New Password (leave blank to keep)
            </label>
            <div className="flex gap-2">
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank to keep"
                className="flex-1"
              />
              <Button variant="neutral" size="icon" className="size-9 shrink-0" onClick={() => setPassword(genTempPassword())}>
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

        </div>

        <DialogFooter>
          <Button variant="neutral" size="sm" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Add Teacher Modal ────────────────────────────────────────────────────────

interface AddTeacherModalProps {
  onClose: () => void
  onCreated: (teacher: StudentItem) => void
}

function AddTeacherModal({ onClose, onCreated }: AddTeacherModalProps) {
  const { getAccessToken } = useAuth()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const handleCreate = async () => {
    const token = getAccessToken()
    if (!token) return
    if (!firstName.trim() || !lastName.trim() || !username.trim() || !password.trim()) {
      toast.error('First Name, Last Name, Username and Password are required.')
      return
    }
    setIsSaving(true)
    try {
      const payload: CreateMemberPayload = {
        givenName: firstName.trim(),
        surName: lastName.trim(),
        username: username.trim(),
        initialPassword: password.trim(),
        role: 'TEACHER',
      }
      const created = await createTeacher(token, payload)
      onCreated(created)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create teacher')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Add Teacher</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                First Name
              </label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                Last Name
              </label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div className="grid gap-1.5">
            <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
              Username
            </label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>

          <div className="grid gap-1.5">
            <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
              Password
            </label>
            <div className="flex gap-2">
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="flex-1"
              />
              <Button variant="neutral" size="icon" className="size-9 shrink-0" onClick={() => setPassword(genTempPassword())}>
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

        </div>

        <DialogFooter>
          <Button variant="neutral" size="sm" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button size="sm" onClick={handleCreate} disabled={isSaving}>
            {isSaving ? 'Creating…' : 'Add Teacher'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Teachers Page ────────────────────────────────────────────────────────────

export default function TeachersPage() {
  const { getAccessToken } = useAuth()
  const { memberRole } = useWorkspace()
  const isTeacherRole = memberRole === 'TEACHER'

  const [teachers, setTeachers] = useState<StudentItem[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [editingTeacher, setEditingTeacher] = useState<StudentItem | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const editSavedRef = useRef(false)

  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [search])

  const load = useCallback((page: number, keyword?: string) => {
    const token = getAccessToken()
    if (!token) return
    setIsLoading(true)
    setError(null)
    fetchMembers(token, 'TEACHER', page, PAGE_SIZE, keyword)
      .then((res) => {
        setTeachers(res.content)
        setTotalElements(res.totalElements)
        setTotalPages(res.totalPages)
        setCurrentPage(page)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load members')
      })
      .finally(() => setIsLoading(false))
  }, [getAccessToken])

  useEffect(() => { load(0, debouncedSearch) }, [load, debouncedSearch])

  const handleEditSave = () => {
    editSavedRef.current = true
    setEditingTeacher(null)
  }

  // Reload list after edit dialog fully closes
  useEffect(() => {
    if (!editingTeacher && editSavedRef.current) {
      editSavedRef.current = false
      load(currentPage, debouncedSearch)
    }
  }, [editingTeacher, load, currentPage, debouncedSearch])

  const handleCreated = () => {
    setShowAddModal(false)
    load(0, debouncedSearch)
  }

  const handleToggle = async (teacher: StudentItem) => {
    const token = getAccessToken()
    if (!token) return
    setTogglingId(teacher.id)
    try {
      await toggleMemberStatus(token, teacher.id, !teacher.isActive)
      setTeachers((prev) =>
        prev.map((t) => (t.id === teacher.id ? { ...t, isActive: !t.isActive } : t)),
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update member status')
    } finally {
      setTogglingId(null)
    }
  }

  const goToPage = (page: number) => load(page, debouncedSearch)

  const pageNumbers: (number | 'ellipsis')[] = []
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pageNumbers.push(i)
  } else {
    pageNumbers.push(0)
    if (currentPage > 2) pageNumbers.push('ellipsis')
    for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages - 2, currentPage + 1); i++) {
      pageNumbers.push(i)
    }
    if (currentPage < totalPages - 3) pageNumbers.push('ellipsis')
    pageNumbers.push(totalPages - 1)
  }

  return (
    <div className={ADMIN_PAGE_CONTENT_CLASSNAME}>
      <div className="mb-6">
        <p className="text-sm text-foreground/60">
          {!isLoading && !error ? `${totalElements} total members` : 'Manage and view teacher members.'}
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center gap-3">
            <span>{error}</span>
            <button
              className="ml-auto text-sm underline underline-offset-2 hover:no-underline"
              onClick={() => load(currentPage, debouncedSearch)}
            >
              Retry
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search teachers..."
            className="pl-9"
          />
        </div>
        {!isTeacherRole && (
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            Add Teacher
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="nb-shadow rounded-base overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-main text-main-foreground hover:bg-main">
              <TableHead>Name</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Status</TableHead>
              {!isTeacherRole && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableLoadingState colSpan={isTeacherRole ? 3 : 4} label="Loading teachers..." />
            ) : teachers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isTeacherRole ? 3 : 4} className="py-16 text-center text-sm text-foreground/40">
                  {search ? 'No teachers match your search.' : 'No members found.'}
                </TableCell>
              </TableRow>
            ) : (
              teachers.map((t) => (
                <TableRow key={t.id} className="hover:bg-main/10">
                  <TableCell className="font-base text-sm">{t.name}</TableCell>

                  <TableCell className="text-sm text-foreground/60">
                    {t.username ? `@${t.username}` : <span className="italic opacity-40">—</span>}
                  </TableCell>

                  <TableCell>
                    {t.isActive ? (
                      <Badge variant="success" className="gap-1.5 px-2.5 py-0.5">
                        <span className="inline-block size-1.5 rounded-full bg-current/70" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="muted" className="gap-1.5 px-2.5 py-0.5">
                        <span className="inline-block size-1.5 rounded-full bg-current/70" />
                        Inactive
                      </Badge>
                    )}
                  </TableCell>

                  {!isTeacherRole && (
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="neutral"
                          size="sm"
                          className="px-3 h-7 text-xs"
                          onClick={() => setEditingTeacher(t)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="neutral"
                          size="sm"
                          disabled={togglingId === t.id}
                          onClick={() => handleToggle(t)}
                          className={`px-3 h-7 text-xs ${
                            t.isActive
                              ? 'border-destructive bg-destructive/10 text-destructive hover:bg-destructive/20'
                              : 'border-green-600 bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/40 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-900/40'
                          }`}
                        >
                          {togglingId === t.id ? '…' : t.isActive ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!isLoading && !error && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-foreground/40">
            Page {currentPage + 1} of {totalPages} &middot; {totalElements} teachers
          </span>
          <Pagination className="w-auto mx-0">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => { e.preventDefault(); if (currentPage > 0) goToPage(currentPage - 1) }}
                  aria-disabled={currentPage === 0}
                  className={currentPage === 0 ? 'pointer-events-none opacity-40' : ''}
                />
              </PaginationItem>

              {pageNumbers.map((p, idx) =>
                p === 'ellipsis' ? (
                  <PaginationItem key={`ellipsis-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href="#"
                      isActive={p === currentPage}
                      onClick={(e) => { e.preventDefault(); goToPage(p) }}
                    >
                      {p + 1}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => { e.preventDefault(); if (currentPage < totalPages - 1) goToPage(currentPage + 1) }}
                  aria-disabled={currentPage >= totalPages - 1}
                  className={currentPage >= totalPages - 1 ? 'pointer-events-none opacity-40' : ''}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {editingTeacher && (
        <EditModal
          teacher={editingTeacher}
          onClose={() => setEditingTeacher(null)}
          onSave={handleEditSave}
        />
      )}

      {showAddModal && (
        <AddTeacherModal
          onClose={() => setShowAddModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}
